// Decisions the cloud sync has to make when it cannot tell what the cloud holds.
//
// Firestore runs here with a persistent local cache (js/services/firebase-core.js).
// That is what lets an arbiter keep working through a dropped connection, but it
// changes what a failed read looks like: offline, getDocs() does not fail — it
// quietly answers from the cache, which may hold nothing at all — and getDoc()
// either answers from the cache or throws. On venue Wi-Fi that comes and goes, both
// happen in the middle of ordinary use.
//
// The rule every helper below follows: when the answer is unknown, treat it as
// unknown. An empty cache is not an empty tournament, a failed list is not an empty
// list, and a stub is not a tournament. Guessing "empty" is what turns bad Wi-Fi
// into lost settings, lost players or a tournament reported as deleted.
//
// Pure and dependency-free, so each decision is tested directly
// (tests/offline-guards.test.js); js/services/cloud-sync.js and js/app.js supply
// the facts.

/**
 * A history entry that names a tournament but does not carry it.
 *
 * The user document keeps only a lightweight copy of the tournament list — id,
 * name, owner and public flag (saveDataToFirestore in js/services/cloud-sync.js) —
 * because the tournaments themselves live in their own documents. On sign-in that
 * copy is overwritten entry by entry from those documents. An entry the refresh did
 * NOT reach (the list failed to load, or answered from an incomplete cache) is left
 * as the stub: its name is right and everything else is missing, so opening it as it
 * stands would show every setting at its default and write those defaults back.
 *
 * Refreshed entries are recognisable by `contentSplit` / a v2 `schemaVersion`, and a
 * full local or legacy entry by carrying its own players array; a stub has neither.
 */
export function isHistoryStub(entry) {
    if (!entry || typeof entry !== 'object') return true;
    if (entry.contentSplit) return false;
    const data = entry.data || {};
    if ((data.schemaVersion || 1) >= 2) return false;
    return !Array.isArray(entry.players);
}

/**
 * The history to adopt on sign-in: the incoming list, except that an incoming stub
 * never replaces a full copy this device already holds of the same tournament.
 *
 * Membership follows the incoming list — it is the cloud's statement of which
 * tournaments exist, and deletions made elsewhere have to arrive. Only the CONTENT
 * of an entry falls back to the local one, and only when the incoming entry is a
 * stub, i.e. when the cloud could not be asked.
 */
export function mergeHistoryKeepingFull(localHistory, incomingHistory) {
    const incoming = Array.isArray(incomingHistory) ? incomingHistory : [];
    const local = Array.isArray(localHistory) ? localHistory : [];
    const localById = new Map(local.map(h => [String(h && h.id), h]));
    return incoming.map(entry => {
        if (!isHistoryStub(entry)) return entry;
        const mine = localById.get(String(entry && entry.id));
        return mine && !isHistoryStub(mine) ? mine : entry;
    });
}

/**
 * Whether the tournament list that came back may be trusted to be COMPLETE — the
 * precondition for anything that removes an entry because it is missing from it.
 * A failed query, or one answered from the offline cache, lists only what happened
 * to be cached.
 */
export function tournamentListIsComplete({ failed, fromCache }) {
    return !failed && !fromCache;
}

/**
 * Whether a tournament assembled from its meta document and content documents
 * actually carries the content, or is a cache that never held it.
 *
 * From the server the assembly is authoritative and this always says yes. From the
 * cache it is checked against the counters the meta document carries for exactly
 * this kind of reader (playerCount, roundsPlayed — written by writeTournamentDoc):
 * a cache that says "no players" about a tournament whose meta says it has twelve
 * did not hold the players, and the tournament must not be opened as if it had none.
 */
export function assembledContentIsComplete(meta, assembled, fromCache) {
    if (!fromCache) return true;
    if (!meta || !assembled) return false;

    const groups = Array.isArray(assembled.groups) && assembled.groups.length > 0 ? assembled.groups : null;
    const playersHeld = groups
        ? groups.reduce((n, g) => n + ((g.players || []).length), 0)
        : (assembled.players || []).length;
    const roundsHeld = groups
        ? groups.reduce((n, g) => Math.max(n, (g.rounds || []).length), 0)
        : (Array.isArray(assembled.rounds) ? assembled.rounds.length : 0);

    const playersExpected = Number(meta.playerCount) || 0;
    const roundsExpected = Number(meta.roundsPlayed) || 0;
    return playersHeld >= playersExpected && roundsHeld >= roundsExpected;
}

/**
 * Whether a snapshot from the live tournament listener may be applied to the open
 * tournament. A snapshot answered from the cache says nothing new about the cloud —
 * it is this device's own old copy — and one that is only this device's pending
 * writes echoing back says nothing at all. Applying either can only move the open
 * tournament BACKWARDS; applying a cache snapshot of a document the cache never held
 * reads as "the owner deleted this tournament".
 */
export function snapshotIsAuthoritative(metadata) {
    if (!metadata) return false;
    return metadata.fromCache !== true && metadata.hasPendingWrites !== true;
}

/**
 * Delay before retrying a cloud save that could not complete, by attempt number
 * (1-based): 5 s doubling to a one-minute ceiling. Null once the attempts are used
 * up — the dirty flag still stands, so the next change, the connection coming back
 * or the next page load picks the save up again.
 */
export const SAVE_RETRY_MAX_ATTEMPTS = 8;
export function saveRetryDelayMs(attempt) {
    if (!Number.isFinite(attempt) || attempt < 1 || attempt > SAVE_RETRY_MAX_ATTEMPTS) return null;
    return Math.min(5000 * 2 ** (attempt - 1), 60000);
}

/**
 * The error a destructive operation throws when it would have had to act on the
 * offline cache. Deleting works from a listing of what exists, and a listing from
 * the cache names only what this device happened to have cached — deleting from it
 * leaves the rest orphaned in the cloud with the parent gone, and nothing can find
 * them again to finish the job. `code: 'offline'` lets the caller tell this apart
 * from a refusal and keep the local copy, since nothing was deleted remotely.
 */
export function offlineError(message) {
    const err = new Error(message);
    err.code = 'offline';
    return err;
}

export function isOfflineError(err) {
    return !!err && err.code === 'offline';
}

/**
 * Whether a failed Firestore read failed for want of a connection — the only kind
 * of failure that leaves the answer UNKNOWN.
 *
 * Other failures carry an answer and must keep meaning what they always meant. The
 * one that matters: reading a tournament document that does not exist yet is
 * refused by the security rules (permission-denied), because the rules read the
 * owner off the document — so for the save path "refused" is how "brand new" looks.
 * Offline, the SDK reports 'unavailable' ("the client is offline"); a request that
 * timed out on a weak signal reports 'deadline-exceeded'.
 */
export function isConnectionFailure(err) {
    if (!err) return false;
    if (err.code === 'unavailable' || err.code === 'deadline-exceeded') return true;
    return /client is offline/i.test(String(err.message || ''));
}
